import {
  FunctionComponent,
  Suspense,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  TableSortLabel,
  Skeleton,
  Tooltip,
  Checkbox,
  IconButton,
  styled,
  TooltipProps,
  tooltipClasses,
} from "@mui/material";
import styles from "./layout/ProjectManagementUI.module.css";
import { useHistory } from "react-router";
import { ProductionListType, ProductionVMType } from "types/ProductionType";
import { MachineType } from "types/MachineType";
import {
  apiChangeProductionDeadline,
  apiGetProductions,
} from "util/network/Productions";
import useStateWithLocalStorage from "hooks/UseStateWithLocalStorage";
import AppStateContext from "contexts/AppStateContext";
import { useQuery, useQueryClient } from "react-query";
import useStateWithSessionStorage from "hooks/UseStateWithSessionStorage";
import { ReactComponent as CommentIcon } from "../../assets/svg/commentOrder.svg";
import { ReactComponent as CommentIconFilled } from "../../assets/svg/commentOrderFilled.svg";
import ProductionDelaySummary from "components/Production/ProductionDelaySummary";
import { CalendarMonth } from "@mui/icons-material";
import DeadlineChangeDialog, {
  DeadlineChangeSubmission,
} from "components/Modals/DeadlineChangeDialog";
import { useToast } from "contexts/ToastContext";
import OrderStatusModal from "components/Modals/OrderStatusModal";
import { OrderStatusEnum } from "types/OrderTypes";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 13,
  },
}));

const ProjectRow = lazy(() => import("./ProjectRow"));

export type SectionType = {
  all: boolean;
  notStart: boolean;
  review: boolean;
  cam: boolean;
  production: boolean;
  postprocess: boolean;
  shipping: boolean;
  done?: boolean;
  // true: only outsourced, false: only non-outsourced, null/undefined: all.
  // (Not `outsourced`: that name was a stored boolean, default false.)
  outsourcedFilter?: boolean | null;
  custom?: boolean;
};

type PropsType = {
  machinesIn?: MachineType[];
  selectedMachines: number[];
  selectedUsers: number[];
  section: SectionType;
  searchValue?: string;
  setRefreshFunc?: (func: () => void) => void;
  orderId?: number;
  // The ticked parts in table order, for actions that live outside the
  // table (the Production Cards print in the page header).
  onSelectionChange?: (productionIds: number[]) => void;
};

const ProjectTable: FunctionComponent<PropsType> = ({
  machinesIn,
  selectedMachines,
  selectedUsers,
  section,
  searchValue,
  setRefreshFunc,
  orderId,
  onSelectionChange,
}) => {
  const { token } = useContext(AppStateContext);
  const history = useHistory();
  const queryClient = useQueryClient();
  const [fetchTimeStamp, setFetchTimeStamp] = useState<number>(0);

  const [debug, setDebug] = useStateWithSessionStorage(false, "debug");

  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useStateWithLocalStorage<number>(
    15,
    "productionPageSize",
  );
  const [sortKey, setSortKey] = useStateWithLocalStorage<
    "deadline_date" | "order_id" | "priority"
  >("deadline_date", "productionSortKey");
  const [sortDirection, setSortDirection] = useStateWithLocalStorage<
    "asc" | "desc"
  >("asc", "productionSortDirection");

  const [testUrl, setTestUrl] = useState<string>();

  const getProductions = (
    page: number,
    pageSize: number,
    section: SectionType,
    sortKey: "deadline_date" | "order_id" | "priority",
    sortDirection: "asc" | "desc",
    selectedMachines: number[],
    selectedUsers: number[],
    search?: string,
    orderId?: number,
  ) => {
    let filter = [];
    if (section.all) {
    } else {
      if (section.notStart) filter.push("todo=true");
      if (section.review) filter.push("review=ACTIVE");
      if (section.cam) filter.push("cam=ACTIVE");
      if (section.production) filter.push("production=ACTIVE");
      if (section.postprocess) filter.push("afterwork=ACTIVE");
      if (section.shipping) filter.push("shipping=ACTIVE");
      if (section.custom) filter.push("custom=ACTIVE");
    }
    if (section.done !== undefined) filter.push(`done=${section.done}`);
    // Tri-state: true narrows to productions with outsourced work, false to
    // those without, null shows everything.
    if (section.outsourcedFilter != null) {
      filter.push(`outsourced=${section.outsourcedFilter}`);
    }

    if (orderId) filter.push(`orderId=${orderId}`);
    if (selectedUsers.length > 0) {
      filter.push(`users=${selectedUsers.join(",")}`);
    }
    if (selectedMachines.length > 0) {
      filter.push(`machines=${selectedMachines.join(",")}`);
    }

    const filter_string = filter.join("&");
    setTestUrl(
      `page=${page}&pageSize=${pageSize}&${filter_string}&sortKey=${sortKey}&sortDirection=${sortDirection}&search=${search}`,
    );
    return apiGetProductions(
      token,
      page + 1,
      pageSize,
      filter_string,
      sortKey,
      sortDirection,
      search,
    ).then((res) => {
      setFetchTimeStamp(Date.now());
      return res;
    });
  };

  const { data, isError, error, isFetching, isLoading, isPreviousData } =
    useQuery<ProductionListType, any>(
      [
        "productions",
        page,
        pageSize,
        section,
        sortKey,
        sortDirection,
        selectedMachines,
        selectedUsers,
        searchValue,
        orderId,
      ],
      () =>
        getProductions(
          page,
          pageSize,
          section,
          sortKey,
          sortDirection,
          selectedMachines,
          selectedUsers,
          searchValue,
          orderId,
        ),
      {
        keepPreviousData: true,
        refetchOnMount: "always",
        staleTime: 5 * 60 * 1000, // 5 minutes
        cacheTime: 30 * 60 * 1000, // 30 minutes
        // getNextPageParam: (lastPage: ProductionListType) => {
        //   if (lastPage.pageSize * lastPage.page < lastPage.total)
        //     return lastPage.page + 1;
        //   return false;
        // },
        // getPreviousPageParam: (firstPage: ProductionListType) => {
        //   if (firstPage.page > 1) return firstPage.page - 1;
        //   return false;
        // },
      },
    );

  // Prefetch the next page!
  // useEffect(() => {
  //   if (data && data?.pageSize * data?.page < data?.total) {
  //     queryClient.prefetchQuery(
  //       [
  //         "productions",
  //         page + 1,
  //         pageSize,
  //         section,
  //         sortKey,
  //         sortDirection,
  //         selectedMachines,
  //         selectedUsers,
  //         searchValue,
  //         orderId,
  //       ],
  //       () =>
  //         getProductions(
  //           page + 1,
  //           pageSize,
  //           section,
  //           sortKey,
  //           sortDirection,
  //           selectedMachines,
  //           selectedUsers,
  //           searchValue,
  //           orderId
  //         )
  //     );
  //   }
  // }, [
  //   data,
  //   page,
  //   pageSize,
  //   section,
  //   sortKey,
  //   sortDirection,
  //   selectedMachines,
  //   selectedUsers,
  //   queryClient,
  //   searchValue,
  //   orderId,
  // ]);

  const [localBlockedOverrides, setLocalBlockedOverrides] = useState<
    Map<number, boolean>
  >(new Map());

  // The parts ticked for a bulk deadline change. Held here rather
  // than in the rows, which are remounted on every refetch (their key carries
  // the fetch timestamp), and cleared whenever the visible page changes -
  // a tick the user can no longer see must not be edited by the header
  // button.
  const { addToast } = useToast();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBulkDeadlineDialog, setShowBulkDeadlineDialog] = useState(false);
  const [savingDeadlines, setSavingDeadlines] = useState(false);

  const pageProductionIds = useMemo<number[]>(
    () => (data?.items ?? []).map((production) => production.id),
    [data],
  );

  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(pageProductionIds);
      const next = new Set(
        Array.from(prev).filter((id) => visible.has(id)),
      );
      return next.size === prev.size ? prev : next;
    });
  }, [pageProductionIds]);

  const handleSelectedChange = (productionId: number, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(productionId);
      else next.delete(productionId);
      return next;
    });
  };

  const allOnPageSelected =
    pageProductionIds.length > 0 &&
    pageProductionIds.every((id) => selectedIds.has(id));
  const someOnPageSelected = pageProductionIds.some((id) =>
    selectedIds.has(id),
  );

  const handleSelectAllOnPage = (selected: boolean) => {
    setSelectedIds(selected ? new Set(pageProductionIds) : new Set());
  };

  // Every visible part of an order, so the order's group header can tick the
  // lot in one click. Grouped by order id rather than by the block of rows
  // under a header: sorted by deadline, one order can open more than one
  // block, and both headers have to agree on what "all of this order" means.
  const productionIdsByOrder = useMemo<Map<number, number[]>>(() => {
    const byOrder = new Map<number, number[]>();
    (data?.items ?? []).forEach((production) => {
      if (!production.orderId) return;
      const ids = byOrder.get(production.orderId) ?? [];
      ids.push(production.id);
      byOrder.set(production.orderId, ids);
    });
    return byOrder;
  }, [data]);

  const handleSelectOrder = (orderIdToSelect: number, selected: boolean) => {
    const ids = productionIdsByOrder.get(orderIdToSelect) ?? [];
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => {
        if (selected) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const selectedProductions = useMemo<ProductionVMType[]>(
    () =>
      (data?.items ?? []).filter((production) =>
        selectedIds.has(production.id),
      ),
    [selectedIds, data],
  );

  // The ticked parts' deadline, when they all share one. Over a mixed set
  // there is no single date, and prefilling any one part's would present a
  // guess as a default.
  const selectedDeadline = useMemo<string | null>(() => {
    const days = new Set(
      selectedProductions.map((production) =>
        production.deadlineDate ? production.deadlineDate.slice(0, 10) : "",
      ),
    );
    if (days.size !== 1) return null;
    const [onlyDay] = Array.from(days);
    return onlyDay || null;
  }, [selectedProductions]);

  const selectedOrderIds = useMemo<number[]>(
    () =>
      Array.from(
        new Set(
          selectedProductions
            .map((production) => production.orderId)
            .filter((id) => !!id),
        ),
      ),
    [selectedProductions],
  );

  // "All parts" only where the table provably holds the whole order: the
  // Sales Order view, unfiltered, with every one of its parts ticked. On the
  // Manufacturing Overview an order's parts can sit on another page or be
  // filtered out by the section, so a tick on every visible row of an order
  // does not prove it, and the dialog lists the ticked parts instead.
  const selectionIsWholeOrder =
    orderId !== undefined &&
    section.all &&
    section.done === undefined &&
    section.outsourcedFilter == null &&
    !searchValue &&
    selectedMachines.length === 0 &&
    selectedUsers.length === 0 &&
    selectedIds.size > 0 &&
    selectedIds.size === data?.total;

  // Table order, not tick order, so a printed stack of cards matches the
  // table.
  useEffect(() => {
    onSelectionChange?.(pageProductionIds.filter((id) => selectedIds.has(id)));
  }, [selectedIds, pageProductionIds, onSelectionChange]);

  const handleBulkDeadlineChange = (submission: DeadlineChangeSubmission) => {
    // The selection is pruned when the visible page changes, which can empty
    // it while this dialog is open. Sending no ids would just earn a 400.
    if (selectedIds.size === 0) {
      setShowBulkDeadlineDialog(false);
      return;
    }
    setSavingDeadlines(true);
    apiChangeProductionDeadline(token, {
      productionIds: Array.from(selectedIds),
      deadline: submission.deadline,
      reasons: submission.reasons,
      comment: submission.comment,
    })
      .then(() => {
        setShowBulkDeadlineDialog(false);
        setSelectedIds(new Set());
        queryClient.invalidateQueries("productions");
      })
      .catch(() => {
        addToast({
          type: "error",
          message: <>Could not change the production deadlines.</>,
        });
      })
      .finally(() => setSavingDeadlines(false));
  };

  const blockedOrderIds = useMemo<Set<number>>(() => {
    const ids = new Set<number>();
    data?.items?.forEach((p) => {
      if (p.blockedBy && p.blockedBy.length > 0) ids.add(p.orderId);
    });
    localBlockedOverrides.forEach((isBlocked, orderId) => {
      if (isBlocked) ids.add(orderId);
      else ids.delete(orderId);
    });
    return ids;
  }, [data, localBlockedOverrides]);

  const handleBlockedChange = (orderId: number, isBlocked: boolean) => {
    setLocalBlockedOverrides((prev) => {
      const next = new Map(prev);
      next.set(orderId, isBlocked);
      return next;
    });
  };

  // EE-268: the complete-order modal lives here, not in the rows. Finishing
  // the last task of several productions on one order in quick succession
  // made every one of those rows find "all productions done" and open its
  // own modal - and each confirm completed the order again (second invoice,
  // second email). One slot: the first order to report done gets the modal,
  // later reports for the same (or another) order are ignored until it is
  // closed.
  const [completeOrderId, setCompleteOrderId] = useState<number | null>(null);
  const handleOrderProductionsDone = useCallback((orderId: number) => {
    setCompleteOrderId((prev) => prev ?? orderId);
  }, []);

  const handleChangePage = (event: any, newPage: any) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setPageSize(event.target.value);
    setPage(0);
  };

  const reformatDate = (date: number | Date | string) => {
    let d = new Date(date);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const projectMemo = useMemo(() => {
    let oldId = 0;
    const rows = data?.items?.map((project) => {
      if (
        project.orderId &&
        project.orderId !== oldId &&
        orderId === undefined
      ) {
        oldId = project.orderId;
        const orderProductionIds =
          productionIdsByOrder.get(project.orderId) ?? [];
        const allOfOrderSelected =
          orderProductionIds.length > 0 &&
          orderProductionIds.every((id) => selectedIds.has(id));
        const someOfOrderSelected = orderProductionIds.some((id) =>
          selectedIds.has(id),
        );
        return (
          <>
            <TableRow
              key={`${project.orderId}`}
              className={
                blockedOrderIds.has(project.orderId)
                  ? styles.projectTitleOpenInquiry
                  : styles.projectTitle
              }
            >
              {/* Ticks every part of this order at once. Sits in the
                  selection column so it lines up with the rows below it. */}
              <TableCell padding="checkbox" sx={{ justifyContent: "center" }}>
                <Checkbox
                  size="small"
                  checked={allOfOrderSelected}
                  indeterminate={someOfOrderSelected && !allOfOrderSelected}
                  onChange={(e) =>
                    handleSelectOrder(project.orderId, e.target.checked)
                  }
                  inputProps={{
                    "aria-label": `Select all parts on order ${project.orderId}`,
                  }}
                />
              </TableCell>
              <TableCell align="center" colSpan={8}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    gap: "10px",
                    marginTop: "1.5em",
                  }}
                >
                  <a
                    href={`/ordrestyring/${project.orderId}`}
                    style={{
                      textDecoration: "none",
                      fontSize: "0.9rem",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    {"#" + project.orderId.toString().padStart(4, "0")}
                  </a>
                  <div style={{ display: "flex", flexDirection: "row" }}>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        paddingBottom: "0.2rem",
                        paddingRight: "0.5rem",
                      }}
                    >
                      {!project.orderMessage ? (
                        <CommentIcon />
                      ) : (
                        <LightTooltip title={project?.orderMessage}>
                          <CommentIconFilled />
                        </LightTooltip>
                      )}
                    </span>
                    <b style={{ fontSize: "0.9rem" }}>
                      {project.orderCompanyName}
                    </b>
                  </div>
                </div>
              </TableCell>
            </TableRow>
            <Suspense
              fallback={
                <TableRow>
                  <TableCell colSpan={10}>
                    <Skeleton variant="rectangular" height={55} />
                  </TableCell>
                </TableRow>
              }
            >
              <ProjectRow
                key={`${project.id}${project.modified}${fetchTimeStamp}`}
                project={project}
                onBlockedChange={handleBlockedChange}
                selectable
                selected={selectedIds.has(project.id)}
                onSelectedChange={handleSelectedChange}
                onOrderProductionsDone={handleOrderProductionsDone}
              />
            </Suspense>
          </>
        );
      } else
        return (
          <Suspense
            fallback={
              <TableRow>
                <TableCell colSpan={12}>
                  <Skeleton variant="rectangular" height={55} />
                </TableCell>
              </TableRow>
            }
          >
            <ProjectRow
              key={`${project.id}${project.modified}${fetchTimeStamp}`}
              project={project}
              selectable
              selected={selectedIds.has(project.id)}
              onSelectedChange={handleSelectedChange}
              onOrderProductionsDone={handleOrderProductionsDone}
            />
          </Suspense>
        );
    });
    return rows;
    // selectedIds is a dependency: the rows render their own tick, so a
    // memo that ignored it would leave the checkboxes stale.
  }, [
    data,
    history,
    handleBlockedChange,
    handleOrderProductionsDone,
    selectedIds,
    productionIdsByOrder,
    fetchTimeStamp,
  ]);

  return (
    <div>
      {debug && testUrl}
      {/* <p>{grouped ? "true" : "false"}</p> */}
      {isError && <div> {error?.message} </div>}
      {/* EE-216: only on the Order View, where the table is one order's
          productions and a roll-up of their delays means something. On the
          global Project Management table the rows span every order, so a
          combined list would be noise. Covers the productions on the current
          page, which is the same set the table below is showing. */}
      {orderId !== undefined && (
        <ProductionDelaySummary productions={data?.items ?? []} />
      )}
      {/* {isLoading || isFetching ? <LinearProgress /> : null} */}
      <TableContainer sx={{ overflowX: "unset" }}>
        {
          <Table
            padding={"checkbox"}
            size="small"
            aria-label="a dense table"
            style={{ tableLayout: "auto" }}
          >
            <TableHead
              sx={{
                backgroundColor: "#075E5B",
                height: "40px",
              }}
            >
              <TableRow key={"header"}>
                {/* Select every part on this page for a bulk
                    deadline change. */}
                <TableCell
                  padding="checkbox"
                  sx={{ backgroundColor: "var(--header-2)" }}
                >
                  <Checkbox
                    size="small"
                    sx={{ color: "#fff" }}
                    checked={allOnPageSelected}
                    indeterminate={someOnPageSelected && !allOnPageSelected}
                    onChange={(e) => handleSelectAllOnPage(e.target.checked)}
                    inputProps={{ "aria-label": "Select all parts on this page" }}
                  />
                </TableCell>
                <TableCell
                  align="left"
                  sx={{ backgroundColor: "var(--header-2)" }}
                ></TableCell>
                <TableCell
                  key={"quantity"}
                  align={"center"}
                  sx={{ backgroundColor: "var(--header-2)" }}
                >
                  <Typography
                    color={"white"}
                    fontWeight={"bold"}
                    variant={"body2"}
                  >
                    Item
                  </Typography>
                </TableCell>
                <TableCell
                  align={"center"}
                  sx={{ backgroundColor: "var(--header-2)" }}
                >
                  <Typography
                    color={"white"}
                    fontWeight={"bold"}
                    variant={"body2"}
                  >
                    Details
                  </Typography>
                </TableCell>
                <TableCell></TableCell>
                <TableCell
                  key={"deadline"}
                  align={"center"}
                  sx={{ backgroundColor: "var(--header-2)" }}
                >
                  <TableSortLabel
                    sx={{ color: "white" }}
                    onClick={() => {
                      setSortKey("deadline_date");
                      setSortDirection((old: "asc" | "desc") =>
                        old === "asc" ? "desc" : "asc",
                      );
                    }}
                    active={sortKey === "deadline_date"}
                    direction={sortDirection}
                  >
                    <Typography
                      noWrap
                      color={"white"}
                      fontWeight={"bold"}
                      variant={"body2"}
                    >
                      Prod. Deadline
                    </Typography>
                  </TableSortLabel>
                  {/* The bulk edit. Same dialog the calendar on a row
                      opens, applied to every ticked part. */}
                  <Tooltip
                    title={
                      selectedIds.size > 0
                        ? `Change deadline for ${selectedIds.size} selected part(s)`
                        : "Select parts to change their deadline"
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        sx={{ color: "#fff" }}
                        disabled={selectedIds.size === 0}
                        onClick={() => setShowBulkDeadlineDialog(true)}
                        aria-label="Change deadline for selected parts"
                      >
                        <CalendarMonth fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  key={"deadline"}
                  align={"center"}
                  sx={{ backgroundColor: "var(--header-2)" }}
                >
                  <Typography
                    noWrap
                    color={"white"}
                    fontWeight={"bold"}
                    variant={"body2"}
                  >
                    Sales Est. Time
                  </Typography>
                </TableCell>
                <TableCell
                  align={"center"}
                  sx={{ backgroundColor: "var(--header-2)" }}
                >
                  <Typography
                    noWrap
                    color={"white"}
                    fontWeight={"bold"}
                    variant={"body2"}
                  >
                    Prod Est. Time
                  </Typography>
                </TableCell>
                <TableCell
                  align={"left"}
                  sx={{ backgroundColor: "var(--header-2)" }}
                >
                  <Typography
                    color={"white"}
                    fontWeight={"bold"}
                    variant={"body2"}
                  >
                    Tasks
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{projectMemo}</TableBody>
          </Table>
        }
      </TableContainer>
      {completeOrderId !== null && (
        <OrderStatusModal
          orderId={completeOrderId}
          status={OrderStatusEnum.COMPLETE}
          isOpen
          onClose={() => setCompleteOrderId(null)}
          onSubmit={() => {}}
        />
      )}
      {showBulkDeadlineDialog && (
        <DeadlineChangeDialog
          open={showBulkDeadlineDialog}
          partCount={selectedIds.size}
          orderIds={selectedOrderIds}
          partIdentifiers={selectedProductions.map(
            (production) => `#${production.productId}`,
          )}
          appliesToAllParts={selectionIsWholeOrder}
          currentDeadline={selectedDeadline}
          isSubmitting={savingDeadlines}
          onClose={() => setShowBulkDeadlineDialog(false)}
          onConfirm={handleBulkDeadlineChange}
        />
      )}
      <TablePagination
        rowsPerPageOptions={debug ? [15, 30, 50, 100, 250, 1000] : [15, 30, 50]}
        component="div"
        count={data?.total ?? 0}
        rowsPerPage={data?.pageSize ?? pageSize}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        // showFirstButton={true}
        // showLastButton={true}
        nextIconButtonProps={{
          disabled: isPreviousData,
        }}
      />
    </div>
  );
};

export default ProjectTable;
